/**
 * Shared Script Library panel.
 *
 * Renders the full library experience (collapsible header, search/filter bar,
 * paginated rows, row dropdown actions, preview modal, save-as-script modal).
 * Designed to drop into any compose surface — currently the Executive
 * Assistant Schedule modal and the Unibox compose modal — so the UX stays
 * identical wherever users insert a script.
 *
 * State and CRUD live entirely inside this component. The host only supplies:
 *  - `tenantId` for the backend calls
 *  - the current compose `draft` (used when the user clicks "Save as script")
 *  - a `placeholderContext` describing how to resolve {{ }} tokens (recipient
 *    first name, sender full + first name)
 *  - an `onLoad` callback that receives the substituted subject/body so the
 *    host can write them into its own compose state.
 *
 * The host is not coupled to EA-specific concepts; the `senderContextLabel`
 * prop just controls the helper copy in the {{ }} examples popover (e.g.
 * "Assistant sender above" vs. "From account above").
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Dropdown,
  Form,
  ListGroup,
  Modal,
  OverlayTrigger,
  Popover,
  Spinner,
} from 'react-bootstrap';
import {
  BookmarkPlus,
  ChevronDown,
  ChevronUp,
  InfoCircle,
  ThreeDotsVertical,
} from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import {
  useCreateEaScheduleMessageScriptMutation,
  useDeleteEaScheduleMessageScriptMutation,
  useGetEaScheduleMessageScriptsQuery,
  useRecordEaScheduleMessageScriptUseMutation,
  useUpdateEaScheduleMessageScriptMutation,
} from '../services/api';
import {
  applyScriptPlaceholders,
  type ScriptPlaceholderContext,
} from '../utils/scriptTemplates';
import { useScriptLibraryAccess } from '../hooks/useScriptLibraryAccess';

// ─────────────────────────────────────────────────────────────────────────────
// Shared types, constants, and helpers (re-exportable so the EA page and any
// other host can also consume them without duplicating their definitions).
// ─────────────────────────────────────────────────────────────────────────────

export const EA_SCRIPT_LIBRARY_CATEGORIES = [
  'Follow Up',
  'Cold Outreach',
  'Contract Renewal',
  'Winback',
  'Internal / Ops',
] as const;

export const EA_SCRIPT_LIBRARY_MAX_TAGS = 15;
export const EA_SCRIPT_TAG_MAX_LEN = 48;
export const EA_SCRIPT_LIBRARY_PAGE_SIZE = 25;

export type EaScriptLibraryStatus = 'active' | 'archived' | 'draft';

export interface EaScheduleMessageScript {
  id: string;
  title: string;
  subject: string;
  body: string;
  updatedAt: string;
  category?: string;
  subcategory?: string;
  tags?: string[];
  useCase?: string;
  taskType?: string;
  tone?: string;
  audience?: string;
  status?: EaScriptLibraryStatus | string;
  createdBy?: string;
  createdAt?: string;
  useCount?: number;
}

export type EaScriptSortMode =
  | 'updated_desc'
  | 'updated_asc'
  | 'created_desc'
  | 'created_asc'
  | 'title_asc'
  | 'title_desc'
  | 'use_count_desc';

export function canonicalEaScriptLibraryCategory(raw: string): string | null {
  const t = raw.trim().toLowerCase();
  if (!t) return null;
  for (const c of EA_SCRIPT_LIBRARY_CATEGORIES) {
    if (c.toLowerCase() === t) return c;
  }
  return null;
}

export function normalizeEaScriptLibraryTagsFromString(raw: string): string[] {
  const parts = raw
    .split(/[,]+/)
    .map((x) => x.trim().toLowerCase().slice(0, EA_SCRIPT_TAG_MAX_LEN))
    .filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    if (seen.has(p)) continue;
    seen.add(p);
    out.push(p);
  }
  return out;
}

export function scriptLibraryCategoryBadgeLabel(raw: string | undefined): string {
  const t = (raw || '').trim();
  if (!t) return '—';
  if (t.toLowerCase() === 'uncategorized') return 'Legacy';
  return t;
}

export function formatEaScriptUpdatedLabel(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function filterAndSortEaScheduleScripts(
  scripts: EaScheduleMessageScript[],
  query: string,
  sort: EaScriptSortMode,
  filters: { category: string; status: string; tagsFilter: string }
): EaScheduleMessageScript[] {
  const q = query.trim().toLowerCase();
  const tagTokens = filters.tagsFilter
    .split(/[,]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

  let list = [...scripts];

  if (filters.category.trim()) {
    const fc = filters.category.trim().toLowerCase();
    list = list.filter((s) => (s.category || '').trim().toLowerCase() === fc);
  }
  if (filters.status) {
    list = list.filter((s) => String(s.status || 'active') === filters.status);
  }
  if (tagTokens.length) {
    list = list.filter((s) => {
      const tagHay = (s.tags || []).join(' ');
      return tagTokens.every((tok) => tagHay.includes(tok));
    });
  }

  if (q) {
    list = list.filter((s) => {
      const hay =
        `${s.title}\n${s.subject}\n${s.body}\n${(s.tags || []).join(' ')}\n${s.useCase || ''}\n${s.category || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }

  const titleCmp = (a: EaScheduleMessageScript, b: EaScheduleMessageScript) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
  const updatedCmp = (a: EaScheduleMessageScript, b: EaScheduleMessageScript) =>
    String(a.updatedAt || '').localeCompare(String(b.updatedAt || ''));
  const createdCmp = (a: EaScheduleMessageScript, b: EaScheduleMessageScript) =>
    String(a.createdAt || a.updatedAt || '').localeCompare(
      String(b.createdAt || b.updatedAt || '')
    );

  switch (sort) {
    case 'title_asc':
      list.sort(titleCmp);
      break;
    case 'title_desc':
      list.sort((a, b) => titleCmp(b, a));
      break;
    case 'updated_asc':
      list.sort(updatedCmp);
      break;
    case 'created_desc':
      list.sort((a, b) => createdCmp(b, a));
      break;
    case 'created_asc':
      list.sort(createdCmp);
      break;
    case 'use_count_desc':
      list.sort((a, b) => {
        const ua = Number(a.useCount) || 0;
        const ub = Number(b.useCount) || 0;
        if (ub !== ua) return ub - ua;
        return updatedCmp(b, a);
      });
      break;
    default:
      list.sort((a, b) => updatedCmp(b, a));
  }
  return list;
}

// RTK Query mishap surface (kept local to avoid a circular import with EA).
function rtkErrorDetail(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object') return fallback;
  const e = err as Record<string, any>;
  const data = e.data || {};
  return (
    data.message ||
    data.error ||
    e.message ||
    fallback
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Panel component
// ─────────────────────────────────────────────────────────────────────────────

interface ScriptLibraryPanelProps {
  tenantId: string;
  /** Current compose draft. Read when the user clicks "Save as script". */
  draft: { subject: string; body: string };
  /**
   * Called when the user picks a script (either by clicking a row or clicking
   * "Load into composer" inside the preview). The subject/body have already
   * been substituted using `placeholderContext`.
   */
  onLoad: (next: { subject: string; body: string; title: string }) => void;
  /** Used to resolve {{ }} tokens at the moment the user picks a script. */
  placeholderContext: ScriptPlaceholderContext;
  /**
   * Optional label used inside the "{{ }} examples" popover so the helper
   * copy points the user at the right field for their context (e.g.
   * "Assistant sender above" vs. "From account above").
   */
  senderContextLabel?: string;
  /**
   * If true, the host should reset the panel state when its parent modal
   * opens — pass a fresh, stable `resetSignal` (e.g. the modal's `show`
   * boolean) to trigger.
   */
  resetSignal?: unknown;
}

export default function ScriptLibraryPanel({
  tenantId,
  draft,
  onLoad,
  placeholderContext,
  senderContextLabel = 'From / Assistant sender above',
  resetSignal,
}: ScriptLibraryPanelProps) {
  const skip = !tenantId;
  const {
    data: messageScriptsRaw = [],
    isLoading: loadingMessageScripts,
    isError: messageScriptsError,
    error: messageScriptsErr,
  } = useGetEaScheduleMessageScriptsQuery({ tenantId }, { skip });
  const messageScripts = messageScriptsRaw as EaScheduleMessageScript[];

  const [createEaScheduleMessageScript, { isLoading: savingScript }] =
    useCreateEaScheduleMessageScriptMutation();
  const [updateEaScheduleMessageScript] = useUpdateEaScheduleMessageScriptMutation();
  const [recordEaScheduleMessageScriptUse] = useRecordEaScheduleMessageScriptUseMutation();
  const [deleteEaScheduleMessageScript] = useDeleteEaScheduleMessageScriptMutation();

  // Browse state
  const [scriptSearchQuery, setScriptSearchQuery] = useState('');
  const [scriptSort, setScriptSort] = useState<EaScriptSortMode>('updated_desc');
  const [scriptFilterCategory, setScriptFilterCategory] = useState('');
  const [scriptFilterStatus, setScriptFilterStatus] = useState('');
  const [scriptFilterTags, setScriptFilterTags] = useState('');
  const [scriptPanelOpen, setScriptPanelOpen] = useState(false);
  const [scriptVisibleLimit, setScriptVisibleLimit] = useState(EA_SCRIPT_LIBRARY_PAGE_SIZE);
  const [scriptPreview, setScriptPreview] =
    useState<EaScheduleMessageScript | null>(null);

  // Save-as-script modal state
  const [scriptSaveOpen, setScriptSaveOpen] = useState(false);
  const [scriptSaveTitle, setScriptSaveTitle] = useState('');
  const [scriptSaveCategory, setScriptSaveCategory] = useState('');
  const [scriptSaveTags, setScriptSaveTags] = useState('');
  const [scriptSaveUseCase, setScriptSaveUseCase] = useState('');

  // Reset state when the host signals a fresh open. Only acts on truthy values
  // so passing `show` directly from a parent <Modal> works.
  useEffect(() => {
    if (!resetSignal) return;
    setScriptSearchQuery('');
    setScriptSort('updated_desc');
    setScriptFilterCategory('');
    setScriptFilterStatus('');
    setScriptFilterTags('');
    setScriptPreview(null);
    setScriptSaveOpen(false);
    setScriptPanelOpen(false);
    setScriptVisibleLimit(EA_SCRIPT_LIBRARY_PAGE_SIZE);
  }, [resetSignal]);

  const hasActiveScriptFilter = Boolean(
    scriptSearchQuery.trim() ||
      scriptFilterCategory ||
      scriptFilterStatus ||
      scriptFilterTags.trim()
  );

  // Auto-open when the user starts narrowing scope; never auto-close so
  // clearing filters leaves them in the panel viewing results.
  useEffect(() => {
    if (hasActiveScriptFilter) setScriptPanelOpen(true);
  }, [hasActiveScriptFilter]);

  useEffect(() => {
    setScriptVisibleLimit(EA_SCRIPT_LIBRARY_PAGE_SIZE);
  }, [
    scriptSearchQuery,
    scriptSort,
    scriptFilterCategory,
    scriptFilterStatus,
    scriptFilterTags,
  ]);

  const scriptCategoryOptions = useMemo(() => {
    const known = new Set<string>(EA_SCRIPT_LIBRARY_CATEGORIES);
    const extras = new Set<string>();
    for (const s of messageScripts) {
      const c = String(s.category || '').trim();
      if (c && !known.has(c)) extras.add(c);
    }
    const merged = [...EA_SCRIPT_LIBRARY_CATEGORIES, ...Array.from(extras)];
    merged.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return merged;
  }, [messageScripts]);

  const scriptLibraryTagSuggestions = useMemo(() => {
    const seen = new Set<string>();
    for (const s of messageScripts) {
      for (const t of s.tags || []) {
        const x = String(t).trim().toLowerCase();
        if (x) seen.add(x);
      }
    }
    return Array.from(seen).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [messageScripts]);

  const filteredSortedScripts = useMemo(
    () =>
      filterAndSortEaScheduleScripts(messageScripts, scriptSearchQuery, scriptSort, {
        category: scriptFilterCategory,
        status: scriptFilterStatus,
        tagsFilter: scriptFilterTags,
      }),
    [
      messageScripts,
      scriptSearchQuery,
      scriptSort,
      scriptFilterCategory,
      scriptFilterStatus,
      scriptFilterTags,
    ]
  );

  // ── Handlers ────────────────────────────────────────────────────────────

  const applyScriptToHost = (s: EaScheduleMessageScript) => {
    const subject = applyScriptPlaceholders(s.subject || '', placeholderContext);
    const body = applyScriptPlaceholders(s.body || '', placeholderContext);
    onLoad({ subject, body, title: s.title });
    toast.success(`Loaded “${s.title}”.`);
  };

  const loadScriptIntoComposer = async (s: EaScheduleMessageScript) => {
    applyScriptToHost(s);
    setScriptPreview(null);
    try {
      await recordEaScheduleMessageScriptUse({ id: s.id, tenantId }).unwrap();
    } catch {
      /* use-count bump is best-effort */
    }
  };

  const setScriptLibraryStatus = async (
    s: EaScheduleMessageScript,
    status: EaScriptLibraryStatus
  ) => {
    try {
      await updateEaScheduleMessageScript({ id: s.id, tenantId, status }).unwrap();
      toast.success(status === 'archived' ? 'Script archived.' : 'Script updated.');
    } catch (e: unknown) {
      toast.error(rtkErrorDetail(e, 'Could not update script'));
    }
  };

  const removeScriptFromLibrary = async (s: EaScheduleMessageScript) => {
    if (!window.confirm(`Delete “${s.title}” from your library?`)) return;
    try {
      await deleteEaScheduleMessageScript({ id: s.id, tenantId }).unwrap();
      toast.success('Script deleted.');
      setScriptPreview(null);
    } catch (e: unknown) {
      toast.error(rtkErrorDetail(e, 'Could not delete script'));
    }
  };

  const openSaveScriptLibraryModal = () => {
    if (!tenantId) {
      toast.error('Tenant not ready; try again.');
      return;
    }
    if (!draft.subject.trim() && !draft.body.trim()) {
      toast.error('Add a subject or message body before saving a script.');
      return;
    }
    const guess = draft.subject.trim().slice(0, 200) || 'Email script';
    setScriptSaveTitle(guess);
    setScriptSaveCategory('');
    setScriptSaveTags('');
    setScriptSaveUseCase('');
    setScriptSaveOpen(true);
  };

  const submitSaveScriptLibrary = async () => {
    if (!tenantId) return;
    const title = scriptSaveTitle.trim();
    if (!title) {
      toast.error('Title is required.');
      return;
    }
    const canonCategory = canonicalEaScriptLibraryCategory(scriptSaveCategory);
    if (!canonCategory) {
      toast.error('Choose a category from the list.');
      return;
    }
    const tagParts = normalizeEaScriptLibraryTagsFromString(scriptSaveTags);
    if (tagParts.length > EA_SCRIPT_LIBRARY_MAX_TAGS) {
      toast.error(`Use at most ${EA_SCRIPT_LIBRARY_MAX_TAGS} distinct tags.`);
      return;
    }
    try {
      await createEaScheduleMessageScript({
        tenantId,
        title: title.slice(0, 200),
        category: canonCategory,
        tags: tagParts,
        useCase: scriptSaveUseCase.trim().slice(0, 4000),
        subject: draft.subject,
        body: draft.body,
      }).unwrap();
      toast.success('Script saved to library.');
      setScriptSaveOpen(false);
    } catch (e: unknown) {
      toast.error(rtkErrorDetail(e, 'Could not save script'));
    }
  };

  // ── Access gate (Phase 2 role/department gating) ────────────────────────
  // Hooks above must always run in stable order; this gate decides only what
  // to render. Default tenant policy is "open to all", so most users sail
  // through. Admins are always allowed regardless of config so they can
  // adjust the policy in Settings.
  const { canSee: canSeeScriptLibrary } = useScriptLibraryAccess();
  if (!canSeeScriptLibrary) return null;

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      <div className="px-3 pt-2 pb-2 border-bottom ea-schedule-scripts-bar ea-script-library-panel">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="ea-script-library-heading fw-semibold text-muted mb-0">
              Script Library
            </span>
            <OverlayTrigger
              trigger="click"
              rootClose
              placement="bottom-start"
              container={typeof document !== 'undefined' ? document.body : undefined}
              overlay={
                <Popover
                  id="ea-schedule-placeholders-guide"
                  className="ea-schedule-placeholders-popover"
                >
                  <Popover.Header as="div" className="py-2 small fw-semibold">
                    {'{{ }}'} placeholders
                  </Popover.Header>
                  <Popover.Body className="small py-2">
                    <p className="text-muted mb-2 mb-0" style={{ fontSize: '0.8rem' }}>
                      These tokens are replaced when you <strong>load a script</strong>.
                      You can type them directly in subject or body too.
                    </p>
                    <hr className="my-2" />
                    <div className="fw-semibold mb-1" style={{ fontSize: '0.72rem' }}>
                      Recipient (from <strong>To</strong>)
                    </div>
                    <dl className="mb-2 mb-0" style={{ fontSize: '0.78rem' }}>
                      <dt className="mb-0">
                        <code className="user-select-all">{'{{firstName}}'}</code>
                      </dt>
                      <dd className="text-muted ms-0 mb-1">
                        First name parsed from To (same as below).
                      </dd>
                      <dt className="mb-0">
                        <code className="user-select-all">{'{{recipientFirstName}}'}</code>
                      </dt>
                      <dd className="text-muted ms-0 mb-0">
                        Same as <code>{'{{firstName}}'}</code>. Example:{' '}
                        <code>Jane Doe &lt;j@x.com&gt;</code> → Jane.
                      </dd>
                    </dl>
                    <hr className="my-2" />
                    <div className="fw-semibold mb-1" style={{ fontSize: '0.72rem' }}>
                      Sender (from <strong>{senderContextLabel}</strong>)
                    </div>
                    <dl className="mb-0" style={{ fontSize: '0.78rem' }}>
                      <dt className="mb-0">
                        <code className="user-select-all">{'{{assistantName}}'}</code>
                      </dt>
                      <dd className="text-muted ms-0 mb-1">Sender first + last name.</dd>
                      <dt className="mb-0">
                        <code className="user-select-all">{'{{assistantFirstName}}'}</code>
                      </dt>
                      <dd className="text-muted ms-0 mb-1">Sender first name only.</dd>
                      <dt className="mb-0">
                        <code className="user-select-all">{'{{senderName}}'}</code>
                      </dt>
                      <dd className="text-muted ms-0 mb-1">
                        Same as full name (alias of <code>{'{{assistantName}}'}</code>).
                      </dd>
                      <dt className="mb-0">
                        <code className="user-select-all">
                          {'{{executiveAssistantName}}'}
                        </code>
                      </dt>
                      <dd className="text-muted ms-0 mb-1">Same as full name (alias).</dd>
                      <dt className="mb-0">
                        <code className="user-select-all">
                          {'{{excutiveAssistantname}}'}
                        </code>
                      </dt>
                      <dd className="text-muted ms-0 mb-0">
                        Same as full name (typo-friendly alias).
                      </dd>
                    </dl>
                  </Popover.Body>
                </Popover>
              }
            >
              <Button
                variant="outline-secondary"
                size="sm"
                type="button"
                aria-label="Show placeholder examples"
                className="ea-script-library-btn"
              >
                <InfoCircle size={12} className="me-1" aria-hidden />
                {'{{ }}'} examples
              </Button>
            </OverlayTrigger>
          </div>
          <div className="d-flex align-items-center gap-1 flex-shrink-0">
            {messageScripts.length > 0 ? (
              <Button
                variant={scriptPanelOpen ? 'outline-secondary' : 'outline-primary'}
                size="sm"
                type="button"
                onClick={() => setScriptPanelOpen((v) => !v)}
                aria-expanded={scriptPanelOpen}
                aria-controls="ea-script-library-body"
                className="ea-script-library-btn"
              >
                {scriptPanelOpen ? (
                  <>
                    <ChevronUp className="me-1" size={12} />
                    Hide library
                  </>
                ) : (
                  <>
                    <ChevronDown className="me-1" size={12} />
                    Browse {messageScripts.length.toLocaleString()} script
                    {messageScripts.length === 1 ? '' : 's'}
                  </>
                )}
              </Button>
            ) : null}
            <Button
              variant="outline-primary"
              size="sm"
              type="button"
              disabled={savingScript}
              onClick={openSaveScriptLibraryModal}
              className="ea-script-library-btn"
            >
              <BookmarkPlus className="me-1" size={12} />
              {savingScript ? 'Saving…' : 'Save as script'}
            </Button>
          </div>
        </div>
        {!scriptPanelOpen ? (
          <div className="small text-muted ea-script-library-hint mb-0">
            {loadingMessageScripts ? (
              <span className="d-inline-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" />
                Loading scripts…
              </span>
            ) : messageScriptsError ? (
              <span className="text-danger">
                {rtkErrorDetail(messageScriptsErr, 'Could not load message scripts.')}
              </span>
            ) : messageScripts.length === 0 ? (
              <span>
                No scripts yet — compose a message, then <strong>Save as script</strong>{' '}
                (title and category required).
              </span>
            ) : (
              <span>
                Click <strong>Browse</strong> to search and filter your script library.
                Placeholders: <strong>{'{{ }}'} examples</strong>.
              </span>
            )}
          </div>
        ) : null}
        {scriptPanelOpen ? (
          <div id="ea-script-library-body">
            <p className="text-muted mb-1 ea-script-library-hint">
              Search and filter, then click a row to preview full content or load into the
              composer. Placeholders: <strong>{'{{ }}'} examples</strong>.
            </p>
            {messageScriptsError ? (
              <Alert variant="warning" className="py-2 small mb-2 mb-sm-1">
                {rtkErrorDetail(messageScriptsErr, 'Could not load message scripts.')}
              </Alert>
            ) : null}
            {loadingMessageScripts ? (
              <div className="d-flex align-items-center gap-2 small text-muted py-1">
                <Spinner animation="border" size="sm" />
                Loading scripts…
              </div>
            ) : messageScripts.length === 0 ? (
              <span className="small text-muted">
                No scripts yet — compose a message, then <strong>Save as script</strong>{' '}
                (title and category required).
              </span>
            ) : (
              <>
                <div className="ea-script-library-filter-bar d-flex flex-wrap align-items-center gap-1 mb-1">
                  <Form.Control
                    type="search"
                    size="sm"
                    placeholder="Search title, subject, body, tags, category…"
                    value={scriptSearchQuery}
                    onChange={(e) => setScriptSearchQuery(e.target.value)}
                    className="ea-schedule-scripts-search"
                    style={{ minWidth: 0, flex: '1 1 180px' }}
                    aria-label="Search script library"
                  />
                  <Form.Select
                    size="sm"
                    value={scriptFilterCategory}
                    onChange={(e) => setScriptFilterCategory(e.target.value)}
                    style={{ minWidth: '8rem', maxWidth: '12rem' }}
                    aria-label="Filter by category"
                  >
                    <option value="">All categories</option>
                    {scriptCategoryOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Select
                    size="sm"
                    value={scriptFilterStatus}
                    onChange={(e) => setScriptFilterStatus(e.target.value)}
                    style={{ minWidth: '7rem' }}
                    aria-label="Filter by status"
                  >
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </Form.Select>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="Tags (comma, match all)"
                    value={scriptFilterTags}
                    onChange={(e) => setScriptFilterTags(e.target.value)}
                    style={{ minWidth: 0, flex: '1 1 140px' }}
                    aria-label="Filter by tags"
                  />
                  <Form.Select
                    size="sm"
                    value={scriptSort}
                    onChange={(e) => setScriptSort(e.target.value as EaScriptSortMode)}
                    className="ea-schedule-scripts-sort"
                    style={{ minWidth: '10rem' }}
                    aria-label="Sort scripts"
                  >
                    <option value="updated_desc">Newest updated</option>
                    <option value="updated_asc">Oldest updated</option>
                    <option value="created_desc">Newest created</option>
                    <option value="created_asc">Oldest created</option>
                    <option value="use_count_desc">Most used</option>
                    <option value="title_asc">Title A–Z</option>
                    <option value="title_desc">Title Z–A</option>
                  </Form.Select>
                </div>
                <div className="ea-script-library-count text-muted">
                  {(() => {
                    const total = messageScripts.length;
                    const matched = filteredSortedScripts.length;
                    const shown = Math.min(matched, scriptVisibleLimit);
                    if (matched === 0) return `0 of ${total.toLocaleString()} scripts`;
                    if (matched === total && shown === total) {
                      return `${total.toLocaleString()} script${total === 1 ? '' : 's'}`;
                    }
                    if (shown < matched) {
                      return `Showing ${shown.toLocaleString()} of ${matched.toLocaleString()}${
                        matched === total ? '' : ` (of ${total.toLocaleString()} total)`
                      } scripts`;
                    }
                    return `${matched.toLocaleString()} of ${total.toLocaleString()} scripts`;
                  })()}
                </div>
                {filteredSortedScripts.length === 0 ? (
                  <span className="small text-muted">No scripts match your filters.</span>
                ) : (
                  <ListGroup
                    variant="flush"
                    className="ea-script-library-rows border rounded overflow-hidden mb-0"
                  >
                    {filteredSortedScripts.slice(0, scriptVisibleLimit).map((s) => {
                      const subj = (s.subject || '').trim();
                      const rawSnippet =
                        subj || (s.body || '').replace(/\s+/g, ' ').trim();
                      const maxSnip = 72;
                      const preview = rawSnippet.slice(0, maxSnip);
                      const previewEllipsis = rawSnippet.length > maxSnip;
                      const st = String(s.status || 'active');
                      const tagShow = (s.tags || []).slice(0, 3);
                      const uc = Number(s.useCount) || 0;
                      return (
                        <ListGroup.Item
                          key={s.id}
                          action
                          as="div"
                          className="ea-script-library-row px-2 py-1"
                          role="button"
                          tabIndex={0}
                          onClick={() => setScriptPreview(s)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setScriptPreview(s);
                            }
                          }}
                        >
                          <div className="d-flex align-items-center gap-2 min-w-0">
                            <div className="flex-grow-1 min-w-0 d-flex align-items-center gap-2 flex-wrap">
                              <span
                                className="ea-script-library-row-title text-truncate"
                                title={s.title}
                              >
                                {s.title}
                              </span>
                              <span className="ea-script-library-row-meta d-flex align-items-center gap-1 flex-shrink-0">
                                <Badge
                                  bg="secondary"
                                  className="ea-script-library-badge-cat"
                                >
                                  {scriptLibraryCategoryBadgeLabel(s.category)}
                                </Badge>
                                <Badge
                                  bg={
                                    st === 'active'
                                      ? 'success'
                                      : st === 'draft'
                                      ? 'warning'
                                      : 'dark'
                                  }
                                  className="ea-script-library-badge-st"
                                >
                                  {st}
                                </Badge>
                              </span>
                            </div>
                            <div className="d-flex align-items-center gap-1 flex-shrink-0">
                              <time
                                className="ea-script-library-row-date text-nowrap"
                                dateTime={s.updatedAt}
                              >
                                {formatEaScriptUpdatedLabel(s.updatedAt) || '—'}
                              </time>
                              {uc > 0 ? (
                                <span className="ea-script-library-use-nudge text-nowrap">
                                  {uc}×
                                </span>
                              ) : null}
                              <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
                                <Dropdown.Toggle
                                  variant="light"
                                  size="sm"
                                  className="ea-script-library-row-menu py-0 px-1 border-0 text-muted"
                                  aria-label="Script actions"
                                >
                                  <ThreeDotsVertical size={14} />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => setScriptPreview(s)}>
                                    Preview
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => void loadScriptIntoComposer(s)}
                                  >
                                    Load into composer
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  {st !== 'archived' ? (
                                    <Dropdown.Item
                                      onClick={() =>
                                        void setScriptLibraryStatus(s, 'archived')
                                      }
                                    >
                                      Archive
                                    </Dropdown.Item>
                                  ) : (
                                    <Dropdown.Item
                                      onClick={() =>
                                        void setScriptLibraryStatus(s, 'active')
                                      }
                                    >
                                      Restore
                                    </Dropdown.Item>
                                  )}
                                  {st !== 'draft' ? (
                                    <Dropdown.Item
                                      onClick={() =>
                                        void setScriptLibraryStatus(s, 'draft')
                                      }
                                    >
                                      Mark draft
                                    </Dropdown.Item>
                                  ) : (
                                    <Dropdown.Item
                                      onClick={() =>
                                        void setScriptLibraryStatus(s, 'active')
                                      }
                                    >
                                      Mark active
                                    </Dropdown.Item>
                                  )}
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => void removeScriptFromLibrary(s)}
                                  >
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </div>
                          {(preview || tagShow.length > 0 || (s.useCase || '').trim()) && (
                            <div className="ea-script-library-row-sub d-flex flex-column gap-0 min-w-0 mt-0">
                              <div className="d-flex align-items-baseline gap-2 min-w-0">
                                {preview ? (
                                  <span
                                    className="text-truncate flex-grow-1 min-w-0"
                                    title={rawSnippet}
                                  >
                                    {preview}
                                    {previewEllipsis ? '…' : ''}
                                  </span>
                                ) : (
                                  <span className="flex-grow-1" />
                                )}
                                {tagShow.length ? (
                                  <span className="ea-script-library-row-tags text-truncate flex-shrink-0">
                                    {tagShow.join(' · ')}
                                    {(s.tags || []).length > 3
                                      ? ` +${(s.tags || []).length - 3}`
                                      : ''}
                                  </span>
                                ) : null}
                              </div>
                              {(s.useCase || '').trim() ? (
                                <div
                                  className="ea-script-library-row-usecase small text-muted text-truncate"
                                  title={(s.useCase || '').trim()}
                                >
                                  <span className="fw-semibold me-1">When:</span>
                                  {(s.useCase || '').trim()}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                )}
                {filteredSortedScripts.length > scriptVisibleLimit ? (
                  <div className="d-flex align-items-center justify-content-center mt-2">
                    <Button
                      variant="link"
                      size="sm"
                      type="button"
                      className="text-decoration-none"
                      onClick={() =>
                        setScriptVisibleLimit((n) =>
                          Math.min(
                            filteredSortedScripts.length,
                            n + EA_SCRIPT_LIBRARY_PAGE_SIZE
                          )
                        )
                      }
                    >
                      Show{' '}
                      {Math.min(
                        EA_SCRIPT_LIBRARY_PAGE_SIZE,
                        filteredSortedScripts.length - scriptVisibleLimit
                      ).toLocaleString()}{' '}
                      more
                      <ChevronDown className="ms-1" size={14} />
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </div>

      {/* Preview modal */}
      <Modal
        show={Boolean(scriptPreview)}
        onHide={() => setScriptPreview(null)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-truncate me-2">
            {scriptPreview?.title || 'Script preview'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {scriptPreview ? (
            <>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <Badge bg="secondary">
                  {scriptLibraryCategoryBadgeLabel(scriptPreview.category)}
                </Badge>
                <Badge
                  bg={
                    scriptPreview.status === 'active'
                      ? 'success'
                      : scriptPreview.status === 'draft'
                      ? 'warning'
                      : 'dark'
                  }
                >
                  {String(scriptPreview.status || 'active')}
                </Badge>
                {Number(scriptPreview.useCount) > 0 ? (
                  <span className="small text-muted">
                    {scriptPreview.useCount} recorded uses
                  </span>
                ) : null}
              </div>
              {(scriptPreview.tags || []).length ? (
                <div className="d-flex flex-wrap gap-1 mb-3">
                  {(scriptPreview.tags || []).map((t) => (
                    <Badge key={t} bg="light" text="dark" className="fw-normal border">
                      {t}
                    </Badge>
                  ))}
                </div>
              ) : null}
              {scriptPreview.useCase ? (
                <div className="mb-3">
                  <div className="small fw-semibold text-muted mb-1">When to use</div>
                  <div
                    className="small border rounded p-2 bg-light"
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {scriptPreview.useCase}
                  </div>
                </div>
              ) : null}
              <div className="mb-2">
                <div className="small fw-semibold text-muted mb-1">Subject</div>
                <div
                  className="small border rounded p-2 font-monospace bg-white"
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {scriptPreview.subject || '—'}
                </div>
              </div>
              <div>
                <div className="small fw-semibold text-muted mb-1">Body</div>
                <div
                  className="small border rounded p-2 font-monospace bg-white ea-script-library-preview-body"
                  style={{
                    whiteSpace: 'pre-wrap',
                    maxHeight: 'min(40vh, 280px)',
                    overflow: 'auto',
                  }}
                >
                  {scriptPreview.body || '—'}
                </div>
              </div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setScriptPreview(null)}>
            Close
          </Button>
          <Button
            variant="primary"
            disabled={!scriptPreview}
            onClick={() =>
              scriptPreview && void loadScriptIntoComposer(scriptPreview)
            }
          >
            Load into composer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Save modal */}
      <Modal show={scriptSaveOpen} onHide={() => setScriptSaveOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Save to Script Library</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-3">
            Category is fixed to a small set so search and future automation stay
            reliable. Tags are optional (comma-separated), stored lowercase, max{' '}
            {EA_SCRIPT_LIBRARY_MAX_TAGS}. Describe <strong>when</strong> to use the
            script so assistants and AI can reason about it later.
          </p>
          <Form.Group className="mb-2">
            <Form.Label className="small fw-semibold">
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              size="sm"
              value={scriptSaveTitle}
              onChange={(e) => setScriptSaveTitle(e.target.value)}
              placeholder="Short name in the library"
              maxLength={200}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small fw-semibold">
              Category <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              size="sm"
              value={scriptSaveCategory}
              onChange={(e) => setScriptSaveCategory(e.target.value)}
              aria-label="Script category"
            >
              <option value="">Select category…</option>
              {EA_SCRIPT_LIBRARY_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small fw-semibold">Tags (optional)</Form.Label>
            <Form.Control
              size="sm"
              list="ea-script-library-tags-datalist"
              value={scriptSaveTags}
              onChange={(e) => setScriptSaveTags(e.target.value)}
              placeholder="vip, renewal, q4"
              aria-label="Script tags"
            />
            <datalist id="ea-script-library-tags-datalist">
              {scriptLibraryTagSuggestions.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
            <Form.Text className="text-muted">
              Existing tags from your library appear as suggestions.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label className="small fw-semibold">When to use this script</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              size="sm"
              value={scriptSaveUseCase}
              onChange={(e) => setScriptSaveUseCase(e.target.value)}
              placeholder="e.g. After a discovery call when they asked for pricing, or when re-engaging a quiet client."
              maxLength={4000}
            />
            <Form.Text className="text-muted">
              Shown in the list as a short “When:” line; optional but strongly
              recommended.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setScriptSaveOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={savingScript}
            onClick={() => void submitSaveScriptLibrary()}
          >
            {savingScript ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
